<template>
    <div class="o-image-input-container">
        <div 
            class="upload-wrapper" 
            :class="{ 'has-image': !!imagefile }"
            :style="styleUploader"
        >
            <input 
                type="file" 
                class="file-input" 
                :id="id" 
                :name="name" 
                accept="image/png, image/jpeg, image/jpg, image/webp" 
                @change="onFileChange" 
                ref="fileInput"
            >
            
            <!-- Image Selected State -->
            <template v-if="imagefile && imagefile !== ''">
                <img :src="imagefile" class="preview-img" ref="previewImage" />
                <div class="change-overlay">
                    <i class="fas fa-camera"></i>
                    <span>Change Photo</span>
                </div>
            </template>
            
            <!-- Placeholder State -->
            <div v-else class="placeholder-content">
                <div class="icon-circle">
                    <i class="fas fa-cloud-upload-alt upload-icon"></i>
                </div>
                <p class="main-text">Click or drag photo here</p>
                <p class="sub-text">PNG, JPG or WEBP (Max 5MB)</p>
            </div>
        </div>

        <!-- Info Message (Only when image exists) -->
        <div v-if="imagefile && imagefile !== ''" class="info-help-text mt-3">
             <i class="fas fa-info-circle me-1"></i>
             <span>Click on the image above to change it</span>
        </div>

        <!-- Error Messages -->
        <div v-if="errorImage" class="error-msg-container">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ errorImage }}</span>
        </div>
        
        <div v-if="payloadError" class="error-msg-container">
            <i class="fas fa-sync-alt"></i>
            <span>{{ payloadErrorMessage }}</span>
        </div>
    </div>
</template>

<script>
export default {
    emits: ['selectImage'],
    props: {
        name: { type: String },
        id: { type: String },
        imageurl: { type: String, default: '' },
        errorPayload: { type: String, default: '' },
        mustBigSize: { type: Boolean, default: false },
        dimensionRules: { type: Array },
        skipCompression: { type: Boolean, default: false }
    },
    name: "OImageInput",
    data: () => ({
        imagefile: "",
        styleUploader: {},
        errorImage: "",
    }),
    mounted() {
        this.imagefile = this.imageurl;
        this.updateStyles(this.imagefile);
    },
    watch: {
        imageurl(newImg) {
            this.imagefile = newImg;
            this.updateStyles(newImg);
        }
    },
    computed: {
        validate() {
            return this.errorImage === "" && this.imagefile !== "";
        },
        payloadError() {
            return this.errorPayload !== '';
        },
        payloadErrorMessage() {
            return this.payloadError ? 'Please refresh browser' : '';
        }
    },
    methods: {
        clear() {
            this.imagefile = "";
            this.styleUploader = {};
            this.errorImage = "";
            this.imagefile = this.imageurl;
            this.updateStyles(this.imagefile);
        },
        onFileChange(e) {
            this.errorImage = "";
            const files = e.target.files || e.dataTransfer.files;

            if (!files.length) return;

            const file = files[0];
            const fileType = file.type;
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            
            if (!validTypes.includes(fileType)) {
                this.removeImage(e);
                alert('Invalid file format. Please use PNG, JPG, or WEBP.');
                return;
            }

            const bytes = file.size;
            const maxSize = 5 * 1024 * 1024;

            if (bytes > maxSize) {
                this.removeImage(e);
                this.errorImage = "File is too large. Maximum size is 5MB.";
                return;
            }

            // Dimension validation
            if (this.dimensionRules && this.dimensionRules.length === 2) {
                const requiredWidth = this.dimensionRules[0];
                const requiredHeight = this.dimensionRules[1];
                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                    if (img.naturalWidth !== requiredWidth || img.naturalHeight !== requiredHeight) {
                        this.errorImage = `Image must be ${requiredWidth}x${requiredHeight}px`;
                        this.removeImage(e);
                    } else {
                        this.finalizeProcess(file);
                    }
                };
            } else {
                this.finalizeProcess(file);
            }
        },
        finalizeProcess(file) {
            if (this.skipCompression || file.size < (1024 * 1024)) {
                this.createImage(file);
            } else {
                this.compressImage(file);
            }
        },
        createImage(file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                this.imagefile = e.target.result;
                this.updateStyles(this.imagefile);
                this.$emit('selectImage', this.imagefile);
            };
        },
        compressImage(file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const img = new Image();
                img.src = e.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const MAX = 800;
                    let w = img.width;
                    let h = img.height;
                    if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
                    else { if (h > MAX) { w *= MAX / h; h = MAX; } }
                    canvas.width = w;
                    canvas.height = h;
                    ctx.drawImage(img, 0, 0, w, h);
                    this.imagefile = canvas.toDataURL(file.type);
                    this.updateStyles(this.imagefile);
                    this.$emit('selectImage', this.imagefile);
                };
            };
        },
        updateStyles(imageSrc) {
            if (!imageSrc) {
                this.styleUploader = {};
                return;
            }
            this.styleUploader = {
                'display': 'inline-flex',
                'min-width': '100px'
            };
        },
        removeImage(e) {
            if (e && e.preventDefault) e.preventDefault();
            this.imagefile = '';
            this.styleUploader = {};
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        }
    }
}
</script>

<style scoped>
.o-image-input-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.upload-wrapper {
    position: relative;
    width: 100%;
    min-height: 200px;
    border: 2px dashed #cbd5e1;
    background-color: #f8fafc;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.2s ease;
}

.upload-wrapper:hover {
    border-color: var(--primary-color, #F93E3E);
    background-color: var(--secondary-color, rgba(249, 62, 62, 0.04));
}

.upload-wrapper.has-image {
    min-height: auto;
    width: auto;
    max-width: 100%;
}

.file-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
}

/* Placeholder State */
.placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    pointer-events: none;
}

.icon-circle {
    width: 56px;
    height: 56px;
    background: #f1f5f9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: #64748b;
    transition: all 0.2s;
}

.upload-wrapper:hover .icon-circle {
    background: var(--secondary-color, rgba(249, 62, 62, 0.08));
    color: var(--primary-color, #F93E3E);
}

.upload-icon {
    font-size: 24px;
}

.main-text {
    font-size: 15px;
    font-weight: 600;
    color: #334155;
    margin: 0 0 4px 0;
}

.sub-text {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
}

/* Has Image State */
.preview-img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 4px;
}

.change-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s ease;
    gap: 8px;
    z-index: 5;
}

.upload-wrapper:hover .change-overlay {
    opacity: 1;
}

.change-overlay i {
    font-size: 24px;
}

.change-overlay span {
    font-size: 13px;
    font-weight: 600;
}

/* Info help text below image */
.info-help-text {
    font-size: 13px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
}

/* Error Info */
.error-msg-container {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ef4444;
    font-size: 13px;
    font-weight: 500;
    margin-top: 12px;
}
</style>